/**
 * 会員ブログ（執筆フロー）の表示ロジック
 * - 目的: 一覧のバッジ・操作ボタン・編集画面のモードを、状態遷移表（設計書 2.2）どおりに一か所で決める
 */

export type MemberPostReviewStatus = 'draft' | 'pending' | 'changes_requested' | 'approved';
export type MemberPostStatus = 'draft' | 'published' | 'archived';
export type MemberPostVisibility = 'public' | 'members_only';

export type MemberPost = {
  id: string;
  title: string;
  excerpt: string | null;
  body: string;
  status: MemberPostStatus;
  visibility: MemberPostVisibility;
  categoryLabel: string | null;
  heroImageId: string | null;
  heroImageUrl: string | null;
  memberId: string | null;
  memberName: string | null;
  reviewStatus: MemberPostReviewStatus;
  reviewComment: string | null;
  submittedAt: string | null;
  reviewedAt: string | null;
  publishedAt: string | null;
  updatedAt: string | null;
  canEdit: boolean;
  canDelete: boolean;
  canSubmit: boolean;
  canWithdraw: boolean;
};

export type MemberPostBadge = { label: string; tone: 'gray' | 'amber' | 'rose' | 'green' | 'slate' };

export function memberPostBadge(reviewStatus: MemberPostReviewStatus, status: MemberPostStatus): MemberPostBadge {
  switch (reviewStatus) {
    case 'draft':
      return { label: '下書き', tone: 'gray' };
    case 'pending':
      return { label: '承認待ち', tone: 'amber' };
    case 'changes_requested':
      return { label: '差し戻し', tone: 'rose' };
    case 'approved':
      // 承認後に運営が非公開にした場合は、会員側からは公開に戻せない
      return status === 'published' ? { label: '公開中', tone: 'green' } : { label: '非公開', tone: 'slate' };
  }
}

export type MemberPostAction = 'edit' | 'view' | 'submit' | 'withdraw' | 'delete';

export function memberPostActions(reviewStatus: MemberPostReviewStatus): MemberPostAction[] {
  switch (reviewStatus) {
    case 'draft':
    case 'changes_requested':
      return ['edit', 'submit', 'delete'];
    case 'pending':
      // 承認待ちは運営が見ている最中なので、取り下げるまで削除もさせない
      return ['view', 'withdraw'];
    case 'approved':
      return ['edit'];
  }
}

export type MemberPostEditorMode = 'new' | 'editable' | 'readonly' | 'published';

export function memberPostEditorMode(reviewStatus: MemberPostReviewStatus | null): MemberPostEditorMode {
  if (reviewStatus === null) return 'new';
  if (reviewStatus === 'pending') return 'readonly';
  if (reviewStatus === 'approved') return 'published';
  return 'editable';
}

/** 公開内容が変わり得る操作のあとに再検証する公開ページ */
export function memberPostRevalidatePaths(postId: string, memberId: string | null): string[] {
  const paths = ['/', '/blog', `/blog/${postId}`];
  if (memberId) paths.push(`/members/${memberId}`);
  return paths;
}

/**
 * 会員種別から「ブログを書ける有料会員か」を判定する。
 *
 * allowlist にしてあるのは、種別が null・未取得・将来増える未知の値のときに
 * 有料会員として扱わないため。`!== 'free'` のような denylist だと、種別が
 * 分からない会員にも執筆導線が出てしまい、開いた先の /profile/posts 側は
 * 同じ allowlist で fail-closed に弾くので、行き止まりになる。
 */
export function isPaidMembershipType(type: string | null | undefined): boolean {
  return type === 'paid_female' || type === 'paid_male';
}

/** 申請は同時に1本まで。対象以外に承認待ちがあれば申請させない */
export function canSubmitAnother(posts: MemberPost[], postId: string): boolean {
  return !posts.some((p) => p.id !== postId && p.reviewStatus === 'pending');
}
